import { useCallback, useEffect, useMemo } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import QRCode from 'qrcode.react'
import { useViewApp } from 'components/Editor/ViewApp'
import { userFlowStateActions } from 'ducks/userFlowState'

import './QRCodeModal.scss'
import aiIcon from './ada.png'

const AIIcon = ({ size = 24 }: { size?: number }) => (
  <img
    src={aiIcon}
    alt="AI"
    style={{ width: size, height: size, objectFit: 'contain' }}
  />
)

const CloseIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
)

const useQuery = () => {
  const { search, pathname } = useLocation()
  const history = useHistory()

  const clearSearch: () => void = useCallback(() => {
    history.push(pathname)
  }, [pathname])

  return useMemo(
    () => ({ url: new URLSearchParams(search), clearSearch }),
    [search, pathname]
  )
}

interface QRCodeModalProps {
  appId: string
}

export const QRCodeModal: React.FC<QRCodeModalProps> = ({
  appId,
}: QRCodeModalProps) => {
  const dispatch = useDispatch()
  const { url: query, clearSearch } = useQuery()
  const { publishUrl } = useViewApp(appId)

  const isAIModalOpen = query.get('showPreviewQRCode') === 'true'

  useEffect(() => {
    dispatch({
      type: userFlowStateActions.MODAL_SET,
      payload: { value: isAIModalOpen },
    })
  }, [isAIModalOpen])

  const getUser = () => {
    const u = sessionStorage.getItem(`initial-application-user-${appId}`);

    if (u === null) return null;

    return JSON.parse(u) as { email: string, password: string };
  }

  const user = getUser();

  if (!isAIModalOpen) {
    return null
  }

  return (
    <div className="qr-code-modal">
      <div className="ai-chat-header">
        <div className="ai-chat-header-left">
          <div className="ai-chat-avatar">
            <AIIcon size={44} />
          </div>
          <div>
            <div className="ai-chat-title">Ada</div>
            <div className="ai-chat-subtitle">AI Assistant</div>
          </div>
        </div>
        <button
          className="ai-chat-close"
          type="button"
          onClick={clearSearch}
          aria-label="Close"
        >
          <CloseIcon />
        </button>
      </div>
      <div className="inner-text top">
        <b>Your app has been generated!</b>
        <br />
        <br /> Click the preview button above to preview it in the Adalo
        simulator, or scan this QR Code with your phone camera to interact with
        the app on your phone!
      </div>
      {publishUrl !== undefined && (
        <div className="qr-code">
          <QRCode value={publishUrl} size={170} />
        </div>
      )}
      {publishUrl !== undefined && (
        <a
          target="_blank"
          rel="noreferrer"
          href={publishUrl}
          className="view-in-browser"
        >
          Open in Browser
        </a>
      )}
      {user !== null && <div className="user-credentials">
        <div className="user-credentials-title">Log in into the application using the following credentials:</div>
        <div className="user-credentials-row">
          <b>Email:</b>
          {user.email}
        </div>
        <div className="user-credentials-row">
          <b>Password:</b>
          {user.password}
        </div>
      </div>}
      <div className="inner-text">
        Ada will return here whenever she can be of further assistance building
        your app!
      </div>
    </div>
  )
}
